import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Storefront, CheckCircle, Clock, CreditCard, Receipt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PayInvoice() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);

  const justPaid = searchParams.get("paid") === "true";

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/pay/${token}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then((data) => { setInvoice(data); setLoading(false); })
      .catch(() => { setError("Invoice not found or expired."); setLoading(false); });
  }, [token]);

  // After Stripe redirect, poll for paid status
  useEffect(() => {
    if (!justPaid || !token) return;
    const poll = setInterval(() => {
      fetch(`${API}/api/pay/${token}/status`)
        .then((r) => r.json())
        .then((d) => {
          if (d.status === "paid") {
            setInvoice((prev) => prev ? { ...prev, status: "paid", paid_at: d.paid_at } : prev);
            clearInterval(poll);
          }
        })
        .catch(() => {});
    }, 2000);
    // Also do an immediate check
    fetch(`${API}/api/pay/${token}/status`)
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "paid") {
          setInvoice((prev) => prev ? { ...prev, status: "paid", paid_at: d.paid_at } : prev);
          clearInterval(poll);
        }
      })
      .catch(() => {});
    return () => clearInterval(poll);
  }, [justPaid, token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/pay/${token}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not create payment session.");
        setPaying(false);
      }
    } catch {
      setError("Payment failed. Please try again.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1E5FEB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Receipt size={28} className="text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-[#121216] mb-2">Invoice Not Found</h1>
          <p className="text-[14px] text-[#4B5563]">{error}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice?.status === "paid";
  const isPaidViaStore = invoice?.paid_via === "in_store";
  const inStorePaid = parseFloat(invoice?.in_store_paid || 0);
  const onlinePaid = parseFloat(invoice?.online_paid || 0);
  const amountDue = invoice?.amount_due != null ? parseFloat(invoice.amount_due) : parseFloat(invoice?.total || 0);
  const hasCredit = inStorePaid > 0.005 || onlinePaid > 0.005;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="pay-invoice-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1E5FEB] flex items-center justify-center shrink-0">
            <Storefront size={20} weight="duotone" className="text-white" />
          </div>
          <div>
            <div className="font-semibold text-[15px] text-[#121216]">{invoice.store_name}</div>
            {invoice.store_phone && <div className="text-[12px] text-[#9CA3AF]">{invoice.store_phone}</div>}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 sm:py-10">
        <div className="max-w-lg mx-auto">
          {/* Status banner */}
          {isPaid && isPaidViaStore ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6 flex items-center gap-4" data-testid="paid-banner">
              <CheckCircle size={32} weight="fill" className="text-emerald-500 shrink-0" />
              <div>
                <div className="font-semibold text-[16px] text-emerald-800">Fully Paid In-Store</div>
                <div className="text-[13px] text-emerald-600">
                  This invoice has been settled via in-store payment. No further action needed.
                </div>
              </div>
            </div>
          ) : isPaid ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6 flex items-center gap-4" data-testid="paid-banner">
              <CheckCircle size={32} weight="fill" className="text-emerald-500 shrink-0" />
              <div>
                <div className="font-semibold text-[16px] text-emerald-800">Payment Received</div>
                <div className="text-[13px] text-emerald-600">
                  Paid on {fmtDate(invoice.paid_at)}. Thank you!
                </div>
              </div>
            </div>
          ) : justPaid ? (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6 flex items-center gap-4">
              <div className="w-6 h-6 border-2 border-[#1E5FEB] border-t-transparent rounded-full animate-spin shrink-0" />
              <div>
                <div className="font-semibold text-[16px] text-blue-800">Processing Payment…</div>
                <div className="text-[13px] text-blue-600">
                  We're confirming your payment. This usually takes a few seconds.
                </div>
              </div>
            </div>
          ) : null}

          {/* Invoice card */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#F3F4F6] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-0.5">Invoice</div>
                <div className="text-[18px] font-semibold text-[#121216]" data-testid="invoice-number">{invoice.invoice_number}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-0.5">Due</div>
                <div className="text-[13px] text-[#4B5563]">{fmtDate(invoice.due_date)}</div>
              </div>
            </div>

            {/* Bill to */}
            <div className="px-5 py-3 border-b border-[#F3F4F6] bg-[#FAFBFC]">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-1">Bill to</div>
              <div className="text-[14px] font-semibold text-[#121216]">{invoice.customer_name}</div>
            </div>

            {/* Device info */}
            {(invoice.device_make || invoice.device_model) && (
              <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center gap-5" data-testid="pay-device-info">
                {invoice.device_make && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-0.5">Make</div>
                    <div className="text-[13px] font-semibold text-[#121216]">{invoice.device_make}</div>
                  </div>
                )}
                {invoice.device_type && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-0.5">Device</div>
                    <div className="text-[13px] font-semibold text-[#121216]">{invoice.device_type}</div>
                  </div>
                )}
                {invoice.device_model && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-0.5">Model</div>
                    <div className="text-[13px] font-semibold text-[#121216]">{invoice.device_model}</div>
                  </div>
                )}
              </div>
            )}

            {/* Line items */}
            <div className="divide-y divide-[#F3F4F6]">
              {(invoice.items || []).map((item, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`pay-item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[#121216]">{item.description}</div>
                    {item.quantity > 1 && (
                      <div className="text-[11px] text-[#9CA3AF]">Qty: {item.quantity}</div>
                    )}
                  </div>
                  <div className="text-[14px] font-mono font-semibold text-[#121216] shrink-0">
                    ${(item.quantity * item.unit_price).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="px-5 py-4 border-t border-[#E5E7EB] bg-[#F8FAFC]">
              {invoice.note && (
                <div className="text-[12px] text-[#4B5563] mb-3 italic">{invoice.note}</div>
              )}
              {hasCredit && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[12px] text-[#9CA3AF] uppercase tracking-wider">Subtotal</div>
                    <div className="text-[15px] font-mono text-[#4B5563] tabular-nums">${invoice.total.toFixed(2)}</div>
                  </div>
                  {inStorePaid > 0.005 && (
                  <div className="flex items-center justify-between mb-3" data-testid="pay-instore-credit">
                    <div className="text-[12px] text-emerald-600 font-semibold uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle size={14} weight="fill" />
                      In-store payment
                    </div>
                    <div className="text-[15px] font-mono font-semibold text-emerald-600 tabular-nums">-${inStorePaid.toFixed(2)}</div>
                  </div>
                  )}
                  {onlinePaid > 0.005 && (
                    <div className="flex items-center justify-between mb-3" data-testid="pay-online-paid">
                      <div className="text-[12px] text-emerald-600 font-semibold uppercase tracking-wider flex items-center gap-1.5">
                        <CheckCircle size={14} weight="fill" />
                        Paid online (card)
                      </div>
                      <div className="text-[15px] font-mono font-semibold text-emerald-600 tabular-nums">-${onlinePaid.toFixed(2)}</div>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold text-[#4B5563] uppercase tracking-wider">{hasCredit ? "Amount Due" : "Total"}</div>
                <div className="text-[24px] font-display font-bold text-[#121216] tabular-nums" data-testid="pay-total">
                  ${amountDue.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Pay button */}
            {!isPaid && amountDue > 0.005 && (
              <div className="px-5 py-5 border-t border-[#E5E7EB]">
                <button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full h-14 bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#93B4F5] text-white text-[16px] font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                  data-testid="pay-now-btn"
                >
                  {paying ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Redirecting to payment…
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} weight="bold" />
                      Pay ${amountDue.toFixed(2)}
                    </>
                  )}
                </button>
                <div className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-[#9CA3AF]">
                  <Clock size={12} />
                  Secure payment powered by Stripe
                </div>
              </div>
            )}
          </div>

          {/* Store info */}
          {invoice.store_address && (
            <div className="text-center mt-6 text-[12px] text-[#9CA3AF]">
              {invoice.store_address}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
